<script setup lang="ts">
import UserSettingsBaseContainer from '../user-settings-base-container.vue';
import GenericError from '~/components/user-interface/generic-error.vue';
import type { IUserInfoExtended } from '~/interfaces/dto/auth/response';

const { $t, $enabled, $trackEvent } = useNuxtApp();
const { $authService } = useServices();
const authStore = useAuthStore();
const personalDetailsOpen = ref<boolean>(true);

const accountConfirmed = ref<boolean>(false);

const displayConfirmAccount = computed(
  () => !accountConfirmed.value && $enabled('myaccount.confirmid'),
);

const displayMobileNumber = computed(() =>
  $enabled('security.isnumberchangeenabled'),
);

const hideAddPersonalDetails = ref<boolean>(false);
const hidePersonalDetails = ref<boolean>(false);
const hideEmail = ref<boolean>(false);
const activeLoader = ref<boolean>(true);

const defaultUserDetails = ref<Partial<IUserInfoExtended>>({
  username: '',
  firstname: '',
  lastname: '',
  languageOption: '',
  dialingCode: '',
  countryCode: '',
  idNumberType: '',
  idNumber: '',
  signupCode: '',
  referralCode: '',
  sourceOfFunds: '',
  dateOfBirth: '',
});

const userDetails = computed(() => {
  return { ...defaultUserDetails.value, ...authStore.currentUser };
});

const errorCode = ref<string>('');
const errorRef = ref<string>('');

const formatBirthday = () => {
  authStore.user.dateOfBirth = new Date(
    userDetails.value.dateOfBirth,
  ).toLocaleDateString('en-GB');
};

const populateUserDetails = async () => {
  activeLoader.value = true;
  handleError();
  try {
    const data = await $authService.getUserExtended();
    if (data?.isSuccessful) {
      formatBirthday();
    } else {
      errorCode.value = `${data?.error?.code ?? 'general-error'}`;
      errorRef.value = `${data?.error?.displayCode ?? 'general-error'}`;
    }
  } catch (error) {
    console.error(error);
  } finally {
    activeLoader.value = false;
  }
};

const handleError = (error: { errorRef: string; errorCode: string } = null) => {
  if (!!error) {
    errorRef.value = error.errorRef;
    errorCode.value = error.errorCode;
  } else {
    errorRef.value = '';
    errorCode.value = '';
  }
};

onMounted(async () => {
  await populateUserDetails();
  $trackEvent('Account', 'MY_ACCOUNT_SETTINGS', {
    to: 'personal details',
  });
});
onUnmounted(() => (accountConfirmed.value = false));
</script>

<template>
  <div class="sm:p-4">
    <LazyUserInterfaceGenericLoader v-if="activeLoader" :standalone="true" />
    <UserSettingsBaseContainer v-else mini-container force-zero-padding stacked>
      <UserProfileManagementConfirmIdentification
        v-if="displayConfirmAccount"
        mode="logged-in"
        :expected-id-number="userDetails.idNumber"
        :expected-id-number-type="userDetails.idNumberType"
        :disabled="!userDetails?.idNumber"
        @account-confirmed="accountConfirmed = true"
        @error="handleError"
      >
        <template #error>
          <UserInterfaceGenericError
            v-if="errorCode"
            :error-code="errorCode"
            class="mb-4"
          >
            <span>{{ $t(`profile-management-${errorCode}`) }}</span>
          </UserInterfaceGenericError>
        </template>
      </UserProfileManagementConfirmIdentification>
      <div v-else>
        <div class="p-2">
          <DesignSystemAccordion
            v-if="!hidePersonalDetails"
            v-model:controller="personalDetailsOpen"
            :close-on-click-outside="false"
            title="personal-details"
            default-open
            bg-layer="bg-base-secondary"
            :classes="{
              content: '!p-0',
            }"
          >
            <UserProfileManagementPersonalDetails v-model="userDetails" />
          </DesignSystemAccordion>
        </div>

        <UserProfileManagementPersonalDetailsAddPersonalDetails
          v-if="!hideAddPersonalDetails"
          :username="userDetails.username"
          :date-of-birth="userDetails.dateOfBirth"
          :firstname="userDetails.firstname"
          :lastname="userDetails.lastname"
          @empty-schema="hideAddPersonalDetails = true"
          @update="populateUserDetails"
        />

        <UserPasswordResetUpdatePassword />
        <UserProfileManagementMobileNumbers
          v-if="displayMobileNumber"
          @update="populateUserDetails"
        />
        <UserProfileManagementEmailAddress
          v-if="!hideEmail"
          :email="userDetails?.email"
          :username="userDetails?.username"
          @update="populateUserDetails"
          @empty-schema="hideEmail = true"
        />
      </div>
      <GenericError
        v-if="errorCode && accountConfirmed"
        state="danger"
        class="max-w-72 mx-auto mb-2"
        :reference-code="errorRef"
      >
        {{ $t(`update-details-${errorCode}`) }}
      </GenericError>
    </UserSettingsBaseContainer>
  </div>
</template>
